import { useEffect, useState } from 'react';

interface FullCompletionCelebrationProps {
  stepTitle: string;
  stepId: number;
  onClose: () => void;
  onNextStep?: () => void;
}

export default function FullCompletionCelebration({
  stepTitle,
  stepId,
  onClose,
  onNextStep,
}: FullCompletionCelebrationProps) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Trigger animation after mount
    setTimeout(() => setShow(true), 50);
  }, []);

  const handleClose = () => {
    setShow(false);
    setTimeout(onClose, 300);
  };

  const handleNextStep = () => {
    setShow(false);
    setTimeout(() => {
      onClose();
      if (onNextStep) onNextStep();
    }, 300);
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
        show ? 'bg-black/50' : 'bg-black/0'
      }`}
      onClick={handleClose}
    >
      {/* Confetti */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {Array.from({ length: 50 }).map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 rounded-full animate-confetti"
            style={{
              left: `${Math.random() * 100}%`,
              top: '-5%',
              backgroundColor: [
                '#14b8a6',
                '#f59e0b',
                '#ef4444',
                '#8b5cf6',
                '#3b82f6',
                '#ec4899',
                '#10b981',
                '#f97316',
              ][i % 8],
              animationDelay: `${Math.random() * 0.5}s`,
              animationDuration: `${2 + Math.random() * 2}s`,
            }}
          ></div>
        ))}
      </div>

      {/* Modal */}
      <div
        className={`bg-white rounded-2xl max-w-md w-full shadow-2xl transform transition-all duration-300 ${
          show ? 'scale-100 opacity-100' : 'scale-90 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-emerald-400 via-teal-500 to-emerald-600 px-6 py-8 text-center rounded-t-2xl overflow-hidden">
          <div className="absolute inset-0 bg-white/10"></div>
          <div className="relative z-10">
            <div className="text-5xl mb-3 animate-bounce-slow">🎉</div>
            <h2 className="text-2xl font-bold text-white mb-2">ステップ完了！</h2>
            <p className="text-sm text-emerald-50">おめでとうございます！</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-5 mb-5 border border-emerald-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center text-white flex-shrink-0">
                <i className="ri-checkbox-circle-fill text-xl"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-emerald-600 font-semibold mb-0.5">STEP {stepId}</p>
                <h3 className="text-base font-bold text-gray-900 truncate">{stepTitle}</h3>
              </div>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              このステップのすべてのチェックリストが完了しました！素晴らしい進捗です。
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5 flex items-start gap-3">
            <div className="w-8 h-8 bg-amber-400 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-flash-line text-white text-sm"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-900 mb-1">次のステップへ</p>
              <p className="text-xs text-amber-700 leading-relaxed">
                この調子で次のステップに進みましょう！研究を着実に前に進めています。
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              閉じる
            </button>
            {onNextStep && (
              <button
                onClick={handleNextStep}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-xl text-sm font-bold hover:from-teal-600 hover:to-emerald-700 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-sm"
              >
                次のステップへ
                <i className="ri-arrow-right-line text-base"></i>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
